// Formatação de dinheiro. Todos os valores em centavos (inteiro) salvo nota.
//
// 0088 — MOEDA POR USUÁRIO. Existem TRÊS dinheiros neste sistema e misturá-los
// é o erro mais caro possível aqui, porque não quebra nada: só mente.
//
//   1. BROKER  — saldo, entrada, payout, lucro, depósito na corretora, stop
//      win/loss. Segue a moeda DA CONTA: BRL na conta legada, USD na conta
//      aberta após a data de corte. Use `formatBrokerMoney*`, que EXIGE a
//      moeda como parâmetro — não existe default, de propósito.
//   2. VENDA   — preço de plano, checkout, Pagfy/Perfectpay, reembolso, gasto
//      de campanha. É BRL SEMPRE, para todo usuário, inclusive para quem opera
//      em dólar. Use `formatSaleMoney*`, que não aceita moeda.
//   3. CUSTO DE LLM — micro-USD de notícias/análises. Categoria própria,
//      `formatUsdMicro`, intocada por esta feature.
//
// A escolha do nome é o mecanismo de auditoria: dá para varrer o repo por
// `formatSaleMoney` num campo de broker (ou vice-versa) e achar o erro por
// grep, em vez de por memória.

export type BrokerCurrency = "BRL" | "USD";

/** Moeda de toda conta anterior ao corte — e o fallback seguro em qualquer
 *  superfície que ainda não conheça o usuário. */
export const LEGACY_BROKER_CURRENCY: BrokerCurrency = "BRL";

/** `US$` e não `$`: em 29/07/2026 o operador leu o formato en-US `"$3.0589"`
 *  como "três mil dólares" — o ponto decimal americano parece separador de
 *  milhar brasileiro. Mantemos os separadores pt-BR e trocamos só o símbolo. */
const CURRENCY_SYMBOL: Record<BrokerCurrency, string> = {
  BRL: "R$",
  USD: "US$",
};

const BRL_FORMAT = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
});

const NUM_FORMAT = new Intl.NumberFormat("pt-BR");

const TWO_DIGITS = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Normaliza uma moeda vinda da rede. Aceita o símbolo cru `"R$"` porque
 *  fixtures antigas do repo mandam isso em vez do código ISO
 *  (`frontend/tests/visual/_helpers/mocks.ts`). Desconhecido → legado. */
export function asBrokerCurrency(
  raw: string | null | undefined,
): BrokerCurrency {
  if (!raw) return LEGACY_BROKER_CURRENCY;
  const normalized = raw.trim().toUpperCase();
  if (normalized === "USD" || normalized === "US$") return "USD";
  return LEGACY_BROKER_CURRENCY;
}

/** Símbolo de exibição da moeda: `R$` ou `US$`. */
export function brokerCurrencySymbol(currency: BrokerCurrency): string {
  return CURRENCY_SYMBOL[currency];
}

// ── (1) DINHEIRO DE BROKER — moeda obrigatória ───────────────────────────────

/** Centavos → `R$ 1.500,00` / `US$ 1.500,00` (espaço normal).
 *
 *  Espaço NORMAL, não NBSP: várias portas de paridade fixam
 *  `screen.getByText("R$ 147,90")` em snapshot. Quem precisa do NBSP do
 *  `Intl({style:'currency'})` usa {@link formatBrokerMoneyIntl}. */
export function formatBrokerMoneyCents(
  cents: number | null | undefined,
  currency: BrokerCurrency,
): string {
  const value = typeof cents === "number" && Number.isFinite(cents) ? cents : 0;
  return formatBrokerMoney(value / 100, currency);
}

/** Valor decimal (NÃO centavos) → `R$ 12,34`; negativo mantém o sinal antes
 *  do símbolo (`-R$ 12,34`). */
export function formatBrokerMoney(
  value: number,
  currency: BrokerCurrency,
): string {
  const symbol = brokerCurrencySymbol(currency);
  if (!Number.isFinite(value)) return `${symbol} 0,00`;
  if (value < 0) return `-${symbol} ${TWO_DIGITS.format(Math.abs(value))}`;
  return `${symbol} ${TWO_DIGITS.format(value)}`;
}

/** Decimal com sinal explícito: `+R$ 1,50` / `-R$ 1,50` / `R$ 0,00` no zero.
 *  O zero SEM sinal é pinado pelos cards da carteira. */
export function formatBrokerMoneySigned(
  value: number,
  currency: BrokerCurrency,
): string {
  const symbol = brokerCurrencySymbol(currency);
  if (value > 0) return `+${symbol} ${TWO_DIGITS.format(value)}`;
  if (value < 0) return `-${symbol} ${TWO_DIGITS.format(Math.abs(value))}`;
  return `${symbol} 0,00`;
}

/** Sempre com sinal: zero e positivo usam `+`. As linhas de trade e as
 *  cadeias de martingale fixam `+R$ 0,00` para entrada pendente. */
export function formatBrokerMoneyAlwaysSigned(
  value: number,
  currency: BrokerCurrency,
): string {
  const symbol = brokerCurrencySymbol(currency);
  const sign = value < 0 ? "-" : "+";
  return `${sign}${symbol} ${TWO_DIGITS.format(Math.abs(value))}`;
}

/** String decimal da rede (`"1500.00"`) → moeda formatada. */
export function formatBrokerMoneyFromDecimalString(
  decimal: string,
  currency: BrokerCurrency,
): string {
  const num = Number.parseFloat(decimal);
  if (!Number.isFinite(num)) return `${brokerCurrencySymbol(currency)} 0,00`;
  return formatBrokerMoney(num, currency);
}

/** Centavos → moeda SEM casas decimais, arredondado. Formato compacto para
 *  cabeçalho, onde os centavos custam altura sem informar nada (reclamação do
 *  operador: o saldo do dashboard estava alto demais). */
export function formatBrokerMoneyInteger(
  cents: number | null | undefined,
  currency: BrokerCurrency,
): string {
  const value = typeof cents === "number" && Number.isFinite(cents) ? cents : 0;
  const units = Math.round(value / 100);
  return `${brokerCurrencySymbol(currency)} ${units.toLocaleString("pt-BR")}`;
}

/** Decimal via `Intl({style:'currency'})` — separador NBSP (U+00A0). */
export function formatBrokerMoneyIntl(
  value: number,
  currency: BrokerCurrency,
): string {
  if (!Number.isFinite(value)) return `${brokerCurrencySymbol(currency)} 0,00`;
  return value.toLocaleString("pt-BR", { style: "currency", currency });
}

/** Agregado de dinheiro de BROKER somado ENTRE usuários.
 *
 *  Existe como função própria — e não como mais uma chamada de
 *  {@link formatBrokerMoneyCents} — para o risco ficar visível por grep: o
 *  backend soma depósitos de todos os clientes de uma campanha, e a partir do
 *  momento em que existirem contas em dólar essa soma mistura moedas.
 *
 *  A decisão do operador (12/08/2026) foi **separar por moeda, sem converter**.
 *  Enquanto o backend não devolver o agregado quebrado por moeda, `currency`
 *  aqui é a moeda dominante da janela e o número é confiável apenas enquanto a
 *  base for de moeda única — que é o caso com a data de corte desligada.
 *  Pendência registrada em `PENDENCIAS.md`. */
export function formatBrokerAggregateCents(
  cents: number | null | undefined,
  currency: BrokerCurrency,
): string {
  return formatBrokerMoneyCents(cents, currency);
}

/** Agregado QUEBRADO POR MOEDA — a forma correta quando a base é mista.
 *
 *  Recebe o mapa que o backend agrupa por `users.broker_currency` e devolve uma
 *  entrada por moeda que tem valor, na ordem canônica (real primeiro, que é a
 *  base legada). Não converte nada: a decisão do operador é somar separado.
 *
 *  Propriedade que importa: com UMA moeda só, devolve exatamente uma entrada e
 *  a tela fica idêntica à de hoje. Isso é o que permite subir com o corte
 *  desligado sem mudar nada visualmente para ninguém.
 *
 *  Zero é omitido de propósito — "US$ 0,00" ao lado de um valor em real não
 *  informa nada e sugere que existe conta em dólar quando não existe. Quando
 *  TUDO é zero, devolve uma entrada só, no legado, para a tela ter o que
 *  mostrar em vez de sumir com o campo. */
export function formatBrokerAggregateByCurrency(
  byCurrency: Partial<Record<BrokerCurrency, number>> | null | undefined,
): ReadonlyArray<{ currency: BrokerCurrency; formatted: string }> {
  const entries = CURRENCY_ORDER.flatMap((currency) => {
    const cents = byCurrency?.[currency] ?? 0;
    if (cents === 0) return [];
    return [{ currency, formatted: formatBrokerMoneyCents(cents, currency) }];
  });
  if (entries.length > 0) return entries;
  return [
    {
      currency: LEGACY_BROKER_CURRENCY,
      formatted: formatBrokerMoneyCents(0, LEGACY_BROKER_CURRENCY),
    },
  ];
}

/** Uma linha só, para caber em célula de tabela e em `title`.
 *
 *  Duas moedas viram `R$ 1.000,00 + US$ 200,00` — o `+` é literal e NÃO é
 *  conversão: são dois totais que convivem. */
export function formatBrokerAggregateInline(
  byCurrency: Partial<Record<BrokerCurrency, number>> | null | undefined,
): string {
  return formatBrokerAggregateByCurrency(byCurrency)
    .map((entry) => entry.formatted)
    .join(" + ");
}

/** Ordem canônica: real primeiro porque é a base legada e a maioria. */
const CURRENCY_ORDER: readonly BrokerCurrency[] = ["BRL", "USD"];

// ── (2) DINHEIRO DE VENDA — BRL travado, sem parâmetro ───────────────────────

/** Centavos de VENDA → `R$ 1.500,00` (NBSP, via `Intl`).
 *
 *  Não aceita moeda porque não existe venda em dólar: plano, checkout e
 *  reembolso são cobrados em real de todo usuário, inclusive de quem opera a
 *  corretora em dólar. */
export function formatSaleMoneyCents(cents: number | null | undefined): string {
  const value = typeof cents === "number" && Number.isFinite(cents) ? cents : 0;
  return BRL_FORMAT.format(value / 100);
}

/** Centavos de VENDA com espaço normal — para os snapshots de paridade. */
export function formatSaleMoneyCentsPlain(
  cents: number | null | undefined,
): string {
  const value = typeof cents === "number" && Number.isFinite(cents) ? cents : 0;
  return `R$ ${TWO_DIGITS.format(value / 100)}`;
}

/** Valor decimal de VENDA (NÃO centavos) → `R$ 12,34`. */
export function formatSaleMoney(value: number): string {
  if (!Number.isFinite(value)) return "R$ 0,00";
  if (value < 0) return `-R$ ${TWO_DIGITS.format(Math.abs(value))}`;
  return `R$ ${TWO_DIGITS.format(value)}`;
}

// ── Não-monetário ────────────────────────────────────────────────────────────

/** Número em pt-BR: 1500 → "1.500". */
export function formatNumber(value: number): string {
  return NUM_FORMAT.format(value);
}

/** Percentual com uma casa: 0.1234 → "12,3%". */
export function formatPct(ratio: number): string {
  return `${(ratio * 100).toFixed(1).replace(".", ",")}%`;
}

// ── (3) CUSTO DE LLM em micro-USD ────────────────────────────────────────────

const USD_MICRO_PER_USD = 1_000_000;

const USD_4_DIGITS = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

/** Micro-USD inteiro → pt-BR: 2 casas a partir de US$ 1, 4 casas abaixo (o
 *  custo por run é sub-centavo e precisa da precisão extra).
 *
 *  Não é dinheiro de broker nem de venda: é telemetria de custo de LLM
 *  (notícias + análises IA), e por isso ficou fora da migração da 0088.
 *
 *  Exemplos: `formatUsdMicro(3_058_900)` → "US$ 3,06";
 *  `formatUsdMicro(63_000)` → "US$ 0,0630". */
export function formatUsdMicro(microUsd: number): string {
  const usd = microUsd / USD_MICRO_PER_USD;
  const digits = Math.abs(usd) >= 1 ? TWO_DIGITS : USD_4_DIGITS;
  return `US$ ${digits.format(usd)}`;
}
